import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateService } from 'src/app/shared/services/date.service';
import { FilterCacheService } from 'src/app/shared/services/filter-cache.service';
import FilterValues from '../../interfaces/ip-filter-values';
import IpSelectInputOption from '../../interfaces/ip-select-input-option';
import { IpRoleService } from '../../services/ip-role.service';
import { MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-ip-filters-table-invoices',
  templateUrl: './ip-filters-table-invoices.component.html',
  styleUrls: ['./ip-filters-table-invoices.component.scss'],
})
export class IpFiltersTableInvoicesComponent {
  @Output() filterEvent = new EventEmitter<FilterValues>();
  @Input() notMoreFilters: boolean = false;
  @Input() forErrors: boolean = false;
  @Input() forInProgress: boolean = false;
  @Input() statusInvoices: Array<[string, string]> = [];
  @Input() selectFirstStatus: boolean = false;
  @Input() disabledSelectStatus: boolean = false;
  @Input() dateInitLabel = 'IP.TABLE.INVOICES.FILTER_FROM_CREATIONDATE';
  @Input() dateEndLabel = 'IP.TABLE.INVOICES.FILTER_TO_CREATIONDATE';
  @Input() dateLoadingInitLabel = 'IP.TABLE.INVOICES.FILTER_FROM_LOADINGDATE';
  @Input() dateLoadingEndLabel = 'IP.TABLE.INVOICES.FILTER_TO_LOADINGDATE';
  @Input() statusLabel = 'IP.TABLE.INVOICES.FILTER_STATUS';
  @Input() withLoadingFilters: boolean = false;
  @Input() filterKey?: string;
  @Input() withoutBrokerFilter: boolean = false;
  @Input() withFiscalIdFilter: boolean = false;

  form!: FormGroup;
  todayDate!: string;
  minDateStart!: string;
  maxDateStart!: string;
  minDateEnd!: string;
  maxDateEnd!: string;
  minLoadingDateStart!: string;
  maxLoadingDateStart!: string;
  minLoadingDateEnd!: string;
  maxLoadingDateEnd!: string;
  userType?: 'business' | 'employee' | 'provider';
  startValuesForm!: any;
  initialized = false;
  isOpenFilter = false;
  brokersSelected: number[] = [];

  optionsStatus: IpSelectInputOption[] = [];
  selectedStatus: string | string[] = '';
  reset = false;

  private selectedStatusChange = new BehaviorSubject<string | string[] | null>(null);
  selectedStatusChange$ = this.selectedStatusChange.asObservable();

  protected _onDestroy = new Subject<void>();

  get createDateFrom() {
    return this.form.get('createDateFrom') as FormControl;
  }
  get createDateTo() {
    return this.form.get('createDateTo') as FormControl;
  }
  get brokerNameBussiness() {
    return this.form.get('brokerNameBussiness') as FormControl;
  }
  get status() {
    return this.form.get('status') as FormControl;
  }
  get loadingDateFrom() {
    return this.form.get('loadingDateFrom') as FormControl;
  }
  get loadingDateTo() {
    return this.form.get('loadingDateTo') as FormControl;
  }
  get fiscalId() {
    return this.form.get('fiscalId') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private ipRoleService: IpRoleService,
    private readonly dateService: DateService,
    private readonly filterCacheService: FilterCacheService
  ) { }

  ngOnInit(): void {
    this.userType = this.ipRoleService.getType();
    this.setDatesStart();
    this.form = this.fb.group({
      createDateFrom: [''],
      createDateTo: ['', this.requiredIfOtherHasValue('createDateFrom')],
      brokerNameBussiness: [''],
      status: '',
      loadingDateFrom: [''],
      loadingDateTo: ['', this.requiredIfOtherHasValue('loadingDateFrom')],
      fiscalId: [''],
    });
    this.startValuesForm = this.form.value;
    this.optionsStatus = this.getOptionsStatus();
    this.loadFilterFromCache();

    this.createDateFrom.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.createDateTo.updateValueAndValidity({ emitEvent: false }));
    this.loadingDateFrom.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.loadingDateTo.updateValueAndValidity({ emitEvent: false }));
  }

  // Marks the control invalid (required) when its paired "from" sibling has a value but this one doesn't
  requiredIfOtherHasValue(otherControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const other = control.parent?.get(otherControlName);
      return other?.value && !control.value ? { required: true } : null;
    };
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  setDatesStart() {
    const today = new Date();
    this.todayDate = this.formatDateArg(today);

    this.maxDateStart = this.todayDate;
    this.maxDateEnd = this.todayDate;
    this.maxLoadingDateEnd = today.toISOString().slice(0, 16);
    this.maxLoadingDateStart = today.toISOString().slice(0, 16);
  }

  onStartDateChange() {
    const startDate = new Date(this.createDateFrom.value);
    this.minDateEnd = startDate.toISOString().split('T')[0];
    const dateToday = new Date();
    let maxDate = new Date();
    if (this.forErrors)
      maxDate = new Date(startDate.setDate(startDate.getDate() + 14));
    else maxDate = new Date(startDate.setMonth(startDate.getMonth() + 3));
    if (maxDate > dateToday) this.maxDateEnd = this.todayDate;
    else this.maxDateEnd = maxDate.toISOString().split('T')[0];
    if (this.createDateTo.value && new Date(this.createDateTo.value) > maxDate)
      this.createDateTo.setValue(maxDate.toISOString().split('T')[0]);    
  }

  onFinishDateChange() {
    const endDate = new Date(this.createDateTo.value);
    this.maxDateStart = endDate.toISOString().split('T')[0];
    let minDate = new Date();
    if (this.forErrors)
      minDate = new Date(endDate.setDate(endDate.getDate() - 14));
    else minDate = new Date(endDate.setMonth(endDate.getMonth() - 3));
    // this.minDateStart = minDate.toISOString().split('T')[0];
  }

  datesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dateFrom = control.get('createDateFrom')?.value;
      const dateTo = control.get('createDateTo')?.value;
      if (!dateFrom || !dateTo) return null;
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);
      if (startDate > endDate) return { initGreater: true };
      const diference = Math.abs(
        (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
      );
      return diference > 31 ? { outOfRange: true } : null;
    };
  }

  onSubmit() {
    this.reset = false;
    let newFilters: FilterValues;
    newFilters = {
      ...(this.selectedStatus &&
        !Array.isArray(this.selectedStatus) && { status: this.selectedStatus }),

      ...(Array.isArray(this.selectedStatus) &&
        this.selectedStatus.length !== 0 && { statusIds: this.selectedStatus }),

      ...(this.createDateFrom.value && {
        createDateFrom: this.formatDate(this.createDateFrom.value),
      }),
      ...(this.createDateTo.value && {
        createDateTo: this.formatDateEndDate(this.createDateTo.value),
      }),
      ...(this.loadingDateFrom.value && {
        loadingDateFrom: this.formatLoadingDate(this.loadingDateFrom.value),
      }),
      ...(this.loadingDateTo.value && {
        loadingDateTo: this.formatLoadingDate(this.loadingDateTo.value),
      }),
      ...(this.brokersSelected.length > 0 && { brokersId: this.brokersSelected }),
      ...(this.withFiscalIdFilter && this.fiscalId.value && { fiscalId: this.fiscalId.value }),
    };

    this.emitFilterEvent(newFilters);
  }

  formatDate(date: string): string | undefined {
    return this.dateService.toInitialDayServerDate(date);
  }

  formatDateEndDate(date: string): string | undefined {
    return this.dateService.toFinalDayServerDate(date);
  }

  newChangesValuesForm() {
    return (
      JSON.stringify(this.form.value) !== JSON.stringify(this.startValuesForm)
    );
  }

  formatDateArg(date: Date) {
    // const today = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  onSelectionChange($event: any) {
    this.onSubmit();
  }

  toggleFilter() {
    this.isOpenFilter = !this.isOpenFilter;
  }

  cleanFilter() {
    this.reset = true;
    this.createDateFrom.setValue('');
    this.createDateTo.setValue('');
    this.brokerNameBussiness.setValue('');
    this.brokersSelected = [];
    this.loadingDateFrom.setValue('');
    this.loadingDateTo.setValue('');
    this.selectedStatus = 'all';
    this.selectedStatusChange.next(this.selectedStatus);
    if (this.withFiscalIdFilter) {
      this.fiscalId.setValue('');
    }
    this.onSubmit();
  }

  getOptionsStatus() {
    var options = this.statusInvoices.map((s) => ({
      value: s[1],
      label: s[0],
    }));
    return options;
  }

  onSelectStatus($event: string | string[]) {
    this.selectedStatus = $event;
  }

  onStartLoadingDateChange() {
    const startDate = new Date(this.loadingDateFrom.value);
    this.minLoadingDateEnd = startDate.toISOString().slice(0, 16);
    const dateToday = new Date();
    let maxDate = new Date(startDate.setMonth(startDate.getMonth() + 3));
    if (maxDate > dateToday) this.maxLoadingDateEnd = dateToday.toISOString().slice(0, 16);
    else this.maxLoadingDateEnd = maxDate.toISOString().slice(0, 16);
    if (this.loadingDateTo.value && this.loadingDateTo.value < this.loadingDateFrom.value) {
      this.loadingDateTo.setValue(this.loadingDateFrom.value);
    }
    if (this.loadingDateTo.value && this.loadingDateTo.value > this.maxLoadingDateEnd) {
      this.loadingDateTo.setValue(this.maxLoadingDateEnd);
    }
  }

  onFinishLoadingDateChange() {
    if (this.loadingDateFrom.value && this.loadingDateTo.value < this.loadingDateFrom.value) {
      this.loadingDateTo.setValue(this.loadingDateFrom.value);
    }
  }

  formatLoadingDate(date: string): string | undefined {
    return this.dateService.toServerDate(date);
  }

  private emitFilterEvent(filters: FilterValues) {
    if (this.filterKey) {
      this.filterCacheService.setFilter(this.filterKey, filters);
    }
    this.filterEvent.emit(filters);
    this.initialized = true;
  }

  private loadFilterFromCache() {
    if (this.filterKey) {
      const cachedFilter = this.filterCacheService.getFilter<FilterValues>(this.filterKey);
      if (cachedFilter) {
        this.form.setValue({
          createDateFrom: this.dateService.fromServerDateStrDate(cachedFilter.createDateFrom, 'yyyy-MM-dd') || '',
          createDateTo: this.dateService.fromServerDateStrDate(cachedFilter.createDateTo, 'yyyy-MM-dd') || '',
          status: cachedFilter.statusIds || cachedFilter.status || '',
          loadingDateFrom: this.dateService.fromServerDateStrDateTime(cachedFilter.loadingDateFrom, 'yyyy-MM-ddTHH:mm') || '',
          loadingDateTo: this.dateService.fromServerDateStrDateTime(cachedFilter.loadingDateTo, 'yyyy-MM-ddTHH:mm') || '',
          brokerNameBussiness: cachedFilter.brokersId || [] ,
          fiscalId: cachedFilter.fiscalId || '',
        });
        if (cachedFilter.statusIds || cachedFilter.status) {
          this.selectedStatus = cachedFilter.statusIds || cachedFilter.status || '';
          this.selectedStatusChange.next(this.selectedStatus);
        }
        if (cachedFilter.brokersId) {
          this.brokersSelected = cachedFilter.brokersId;
        }
        this.emitFilterEvent(cachedFilter);
      }
    }
  }

  selectBroker(ev: MatSelectChange){
    this.brokersSelected = ev.value
  }
}
